import React, { useState } from 'react';
import { HeadingSBold, BodyS, BodySBold, BodyM, BodyMBold } from 'components/Typography/Typography';
import { Table, TableCell, TableContainer, TableRow, makeStyles } from '@material-ui/core';
import Button from 'components/CustomButtons/Button';
import styles from 'assets/jss/material-dashboard-pro-react/views/bwodExtensionModalStyle';
import { dynamicTranslate, translate } from 'components/Translation';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import moment from 'moment';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { useSelector } from 'react-redux';
import { getBwodLabels, getCommonLabels } from 'redux/modules/dynamicLabels/selectors';
import { SingleCommitmentDetails } from 'utils/api/models/payments/singleCommitmentDetails';
import OtpDialog from 'views/Auth/common/OtpDialog';
import { showLoadingIndicator, hideLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { setToast } from 'redux/modules/toast/actions';
import { dispatch } from 'redux/store';
import { finalize } from 'rxjs/operators';
import { UsersService } from 'services/users/users';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { userContactNumberSelector } from 'redux/modules/session/selectors';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

// @ts-ignore

interface IBWODExtensionModal {
	hideModal: Function;
	loading: boolean;
	bwodPlanExtend: boolean;
	addOnServices: types.bwod.IService[];
	currentPlan?: types.bwod.CurrentPlan;
	submitRequest: (numberOfMonths: string, productId: string, productName: string, otp: string) => void;
	activeCommitMents?: SingleCommitmentDetails;
	isWrongOtp: boolean;
	setIsWrongOtp: (t: boolean) => void;
}

export const BWODExtensionModal = ({
	hideModal,
	addOnServices,
	currentPlan,
	submitRequest,
	loading,
	bwodPlanExtend,
	activeCommitMents,
	isWrongOtp,
	setIsWrongOtp,
}: IBWODExtensionModal) => {
	const useStyles = makeStyles(styles);
	const [addOnMonthDuration, setAddOnMonthDuration] = useState('1');
	const [productId, setProductId] = useState<string>(addOnServices[0].productId);
	const [productName, setProductName] = useState<string>(
		`${addOnServices[0].productName} ${addOnServices[0].downloadSpeed}`
	);
	const [isOTPStep, setIsOtpStep] = useState(false);
	const [otp, setOtp] = useState('');
	const classes = useStyles();
	const bwodLabels = useSelector(getBwodLabels);
	const commonLabels = useSelector(getCommonLabels);
	const userNumber = useSelector(userContactNumberSelector);

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		UsersService.requestForOtp(OperationContextEnum.ADDBANDWIDTHONDEMAND, userNumber)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				() => {
					setToast({
						error: false,
						label: translate('otp.token.sent'),
					});
					setIsOtpStep(true);
				},
				(error) => {
					if (error.response.code === ERROR_VALIDATING_TOKEN) {
						setIsWrongOtp(true);
					}
				}
			);
	};

	return (
		<>
			<CustomDialog
				header={bwodLabels.extenstionModalHeading}
				className={classes.extensionModal}
				disableBackdropClick={true}
				isOpen={true}
				width={700}
				dialogBody={
					<ConditionalRender
						show={loading}
						onTrue={() => <CustomCircularProgress loading text="" transparent />}
						onFalse={() => (
							<div className={classes.dialogBodyWrapper}>
								<div className={classes.basePlanWrapper}>
									<HeadingSBold>{bwodLabels.basePlanHeading}</HeadingSBold>
									<div className={classes.basePlanDetails}>
										<TableContainer>
											<Table>
												<TableRow
													aria-label={`Your base plan ${
														currentPlan?.name
													} which will ends on ${moment(currentPlan?.endDate).format(
														'Do MMMM YYYY'
													)}`}
												>
													<TableCell align="left">
														<BodyS>{bwodLabels.planName}</BodyS>
													</TableCell>
													<TableCell align="left">
														<BodySBold>{activeCommitMents?.name}</BodySBold>
													</TableCell>
												</TableRow>
												<TableRow>
													<TableCell align="left">
														<BodyS>{bwodLabels.basePlanEnd}</BodyS>
													</TableCell>
													<TableCell align="left">
														<BodySBold>
															{moment(currentPlan?.endDate).format('Do MMMM YYYY')}
														</BodySBold>
													</TableCell>
												</TableRow>
											</Table>
										</TableContainer>
									</div>
								</div>
								<div className={classes.addOnWrapper}>
									<div className={classes.addOnHeading}>
										<HeadingSBold disableMargin>
											{dynamicTranslate(
												bwodLabels.addOnSpeed,
												addOnServices[0]?.downloadSpeed || ''
											)}
										</HeadingSBold>
									</div>
									<div className={classes.addOnSelect}>
										<BodyM disableMargin>{bwodLabels.selectDureation}</BodyM>
										<div className={classes.radioOptions}>
											{addOnServices.map((service, index) => (
												<div key={service.productId}>
													<CustomRadio
														label={dynamicTranslate(
															index === 0 ? bwodLabels.month : bwodLabels.months,
															index + 1
														)}
														value={`${index + 1}`}
														checked={addOnMonthDuration === `${index + 1}`}
														onChange={() => {
															setAddOnMonthDuration(`${index + 1}`);
															setProductId(service.productId);
															setProductName(
																`${service.productName} ${service.downloadSpeed}`
															);
														}}
													/>
												</div>
											))}
										</div>
									</div>
									<div className={classes.addOnDetails}>
										<TableContainer>
											<Table>
												<TableRow
													aria-label={`Your add-on plan ${
														addOnServices[0]?.downloadSpeed
													} which will ends on ${moment()
														.add(parseInt(addOnMonthDuration), 'M')
														.format('Do MMMM YYYY')} and cost is ${dynamicTranslate(
														commonLabels.price,
														parseInt(addOnServices[0]?.activationCost) *
															parseInt(addOnMonthDuration)
													)} and ${addOnServices[0]?.vat}% vat extra`}
												>
													<TableCell align="left">
														<BodyS>{bwodLabels.addOnCostHeading}</BodyS>
													</TableCell>
													<TableCell align="left">
														<span className={classes.addOnDateWrapper}>
															<BodyMBold>
																{dynamicTranslate(
																	commonLabels.price,
																	parseInt(addOnServices[0]?.activationCost) *
																		parseInt(addOnMonthDuration)
																)}
															</BodyMBold>
															<BodyM customClassName={classes.leftSpace}>
																{dynamicTranslate(
																	bwodLabels.vatExtra,
																	addOnServices[0]?.vat
																)}
															</BodyM>
														</span>
													</TableCell>
												</TableRow>
												<TableRow>
													<TableCell align="left">
														<BodyS>{bwodLabels.addOnEndsOn}</BodyS>
													</TableCell>
													<TableCell align="left">
														<span className={classes.addOnDateWrapper}>
															<BodyMBold>
																{moment()
																	.add(
																		parseInt(addOnMonthDuration) === 1
																			? '30'
																			: '60',
																		'day'
																	)
																	.format('Do MMMM YYYY')}
															</BodyMBold>
															<BodyM customClassName={classes.leftSpace}>
																{bwodLabels.backOriginal}
															</BodyM>
														</span>
													</TableCell>
												</TableRow>
											</Table>
										</TableContainer>
									</div>
								</div>
								<div>
									<Button
										className={classes.submitAddOn}
										color="primary-light"
										data-testid="submitBwodRequest"
										onClick={requestForOtp}
										aria-label="Submit to raise the request"
									>
										{bwodPlanExtend ? bwodLabels.submitExtReq : commonLabels.submit}
									</Button>
								</div>
							</div>
						)}
					/>
				}
				hideDialog={() => hideModal(false)}
			/>
			<OtpDialog
				otp={otp}
				setOtp={setOtp}
				isOpen={isOTPStep}
				isWrongOtp={isWrongOtp}
				onSubmit={() => submitRequest(addOnMonthDuration, productId, productName, otp)}
				onClose={() => setIsOtpStep(false)}
				heading={translate('number.settings.password.sent')}
				setIsWrongOtp={setIsWrongOtp}
				loading={loading}
				sendOtp={requestForOtp}
			/>
		</>
	);
};
